import { Injectable } from '@angular/core';
import { ElementType } from '../models/ToolboxModel';
import { IDimensionsGroup } from '../interfaces/IDimensionsGroup';
import { Tags, SvgParams, DimensionLineParams, GLASS_PART_PADDING, Translations } from '../constants/constants';
import { GlassPartDiamond } from '../models/glasses/glassDiamondPart';
import { Common } from '../models/common';
import { DimensionLine } from '../models/dimensions/dimension-line.model';
import { DimensionText } from '../models/dimensions/dimension-text.model';
import { GlassWallDiamond } from '../models/glasses/glassWallDiamond.model';
import { IGlassSpec } from '../interfaces/specs/IGlassSpec';
import { WallProfile } from '../models/profiles/wall.model';
import { WallGutterProfile } from '../models/profiles/wall-gutter.model';
import { Glass } from '../models/glasses/glass.model';
import { RoofWindow } from '../models/glasses/roofWindow.model';
import { RoofVentilator } from '../models/glasses/roofVentilator.model';
import { RoofGutter } from '../models/glasses/roofGutter.model';
import { ColumnProfile } from '../models/profiles/column.model';
import { ProfileService } from './profile.service';
import { ProjectService } from './project.service';
import { LED } from '../models/profiles/leds.model';
import { ElementSide } from '../interfaces/IElementSide';
import { AreaType, CollectionName, RectName } from '../interfaces/IAreaType';
import { MarquiseTop } from '../models/marquises/marquise-top.model';
import { MarquiseVertical } from '../models/marquises/marquise-vertical.model';
import { Door } from '../models/doors/door.model';
import { IRect } from '../interfaces/IRect';
import { Profile } from '../models/profiles/profile.model';
import { GlassWall } from '../models/glasses/glassWall.model';
import { GlassPart } from '../models/glasses/glassPart.model';
import { VerticalElement } from '../models/vertical-element';

@Injectable({
	providedIn: 'root'
})
export class DimensionsService {

	private leftFrontHeightIx: number = -1;
	private leftColHeightIx: number = -1;
	private rightFrontHeightIx: number = -1;
	private rightColHeightIx: number = -1;

	private _showArea: boolean = true;
	private _showElements: boolean = true;

	private foundHeight: number;

	constructor(
		private profileService: ProfileService,
		private projectService: ProjectService
	) { }

	public displayDimensions(showArea: boolean = true, showElements: boolean = true) {
		if (this._showArea != showArea || this._showElements != showElements) {
			this.projectService.clearCollections(ElementType.Dimension);
		}
		this._showArea = showArea;
		this._showElements = showElements;

		if (this.profileService.roofElements[ElementType.Dimension].length === 0) {

			this.displayDimensionsOnRoof();

			const frontH = this.projectService.template.getFrontSize().height;
			const front = this.profileService.getFrontOnSide(this.profileService.frontElements, Tags.LEFT_FRONT_TAG);
			this.displayDimensionsOnStraight(AreaType.Front, frontH, front, Tags.LEFT_COLUMN_TAG);

			if (this.projectService.template.isStandalone) {
				const rearH = this.projectService.template.getRearSize().height;
				const rear = this.profileService.getRearOnSide(this.profileService.rearElements, Tags.LEFT_REAR_TAG);
				this.displayDimensionsOnStraight(AreaType.Rear, rearH, rear, Tags.LEFT_REAR_COLUMN_TAG);
			}

			this.displayDimensionsOnLeftSide();
			this.displayDimensionsOnRightSide();
		}
	}

	//#region Glass wall | diamond

	public getWidthDimensionsForGlassWall(glassWall: GlassWallDiamond | GlassWall, area: AreaType): IDimensionsGroup[] {
		var dims: IDimensionsGroup[] = [];

		var walls = this.profileService.getVerticals(area, ElementType.GlassWall);
		var diams = this.profileService.getVerticals(area, ElementType.GlassWallDiamond);
		var wallsCount = walls.length + diams.length;
		if (wallsCount > 1 && walls[0].width == diams[0].width && glassWall.type == ElementType.GlassWallDiamond) {
			return dims;
		}
		

		var distance = 6;

		if (wallsCount > 1 && glassWall.type == ElementType.GlassWallDiamond) {
			distance = 36;
		}

		var x1 = glassWall.points[SvgParams.RIGHT_BOTTOM_POINT_NUM].x;
		const y1 = this.profileService.getCornerColumn(area)['rectOn' + area].y + this.profileService.getCornerColumn(area)['rectOn' + area].h + distance;
		var x2 = glassWall.points[SvgParams.LEFT_BOTTOM_POINT_NUM].x;
		const y2 = y1;

		if (glassWall.leftFrame) {
			x2 -= glassWall.leftFrame.getRect(area).w;
		}
		if (glassWall.rightFrame){
			x1 += glassWall.rightFrame.getRect(area).w;
		}

		const newDimensionLine: DimensionLine = new DimensionLine(x1, y1, x2, y2, DimensionLineParams.SECOND_LEVEL_DIMENSION, ElementSide.Bottom);

		const x = x2 + (x1 - x2) / 2;
		const y = y1 + 15;
		const length = Common.round((glassWall.points[SvgParams.RIGHT_BOTTOM_POINT_NUM].x - glassWall.points[SvgParams.LEFT_BOTTOM_POINT_NUM].x) / SvgParams.SCALE);
		const newDimensionText: DimensionText = new DimensionText(x, y, length, false);

		dims.push({ text: newDimensionText, line: newDimensionLine });

		if (Math.round(glassWall.width) != Math.round(glassWall.maxWidth)) {
			dims.push(...this.getSpaceWidthDimensionsForGlassWall(glassWall, area));
		}

		return dims;
	}

	public getHeightDimensionsForGlassWall(glassWall: GlassWallDiamond | GlassWall, area: AreaType): IDimensionsGroup[] {
		var dims: IDimensionsGroup[] = [];
		const column = this.profileService.getCornerColumn(area);
		const rear = this.profileService.roofElements[ElementType.WallProfile].find((w: WallProfile) => w[RectName.get(area)] != null);
		const paddingTop = glassWall.topFrame ? glassWall.topFrame.getRect(area).h : 0;
		const paddingBottom = glassWall.bottomFrame ? glassWall.bottomFrame.getRect(area).h : 0;

		if (glassWall instanceof GlassWallDiamond || (glassWall instanceof GlassWall && area == AreaType.Right)) {
			var lx1 = 0;
			if (area == AreaType.Left) {
				lx1 = rear.rectOnLeft.x - 6;
			} else {
				lx1 = column[RectName.get(area)].x - 6;
			}
			var ly1 = glassWall.points[SvgParams.LEFT_TOP_POINT_NUM].y - paddingTop;
			var ly2 = glassWall.points[SvgParams.LEFT_BOTTOM_POINT_NUM].y + paddingBottom;

			const lx = lx1 - 6;
			const ly = ly1 + (ly2 - ly1) / 2;
			const leftHeight = Common.round((ly2 - ly1) / SvgParams.SCALE);

			if (leftHeight > 51) {
				const leftD = this.createDimension(lx1, lx1, ly2, ly1, DimensionLineParams.SECOND_LEVEL_DIMENSION, lx, ly, leftHeight, true, ElementSide.Left);
				dims.push(leftD);
			}
		}
		if (glassWall instanceof GlassWallDiamond || (glassWall instanceof GlassWall && area != AreaType.Right)) {
			var rx1 = 0;
			if (area == AreaType.Left) {
				rx1 = column[RectName.get(area)].x + column[RectName.get(area)].w + 6;
			} else {
				rx1 = column[RectName.get(area)].x + column[RectName.get(area)].w + 6;
			}

			const ry1 = glassWall.points[SvgParams.RIGHT_BOTTOM_POINT_NUM].y + paddingBottom;
			const ry2 = glassWall.points[SvgParams.RIGHT_TOP_POINT_NUM].y - paddingTop;

			const rx = rx1 + 12;
			const ry = ry2 + (ry1 - ry2) / 2;
			const rightHeight = Common.round((ry1 - ry2) / SvgParams.SCALE);

			if (rightHeight > 51) {
				const rightD = this.createDimension(rx1, rx1, ry2, ry1, DimensionLineParams.FIRST_LEVEL_DIMENSION, rx, ry, rightHeight, true, ElementSide.Right);
				dims.push(rightD);
			}
		}

		if (glassWall instanceof GlassWall && Math.round(glassWall.height) != Math.round(glassWall.maxHeight)) {
			dims.push(...this.getSpaceHeightDimensionsForGlassWall(glassWall, area));
		}

		return dims;
	}

	public getDimentionsForGlassParts(parts: GlassPartDiamond[] | GlassPart[], area: AreaType): IDimensionsGroup[] {		
		var dimensionsForGlassParts: IDimensionsGroup[] = [];
		parts?.forEach(part => {
			var rect = part[RectName.get(area)];
			if (!rect) {
				rect = part.rect;
			}
			
			var x = rect.x + rect.w / 2;
			var y = part.master[RectName.get(area)].y + part.master[RectName.get(area)].h / 2;
			var anchor = "middle";

			if (part instanceof GlassPartDiamond) {
				const leftH = GlassPartDiamond.getLeftHeight(part.points);
				const rightH = GlassPartDiamond.getRightHeight(part.points);
				if (rightH > leftH && leftH <= 200) {
					x = rect.x + rect.w - 5;
					y = rect.y + rect.h - 5;
					anchor = "end";
					// bottom right
				} else if (leftH > rightH && rightH <= 200) {
					x = rect.x + 5;
					y = rect.y + rect.h - 5;
					anchor = "start";
					// botton left
				}
			}

			var width = part.width;
			var height = part.height;
			var partDim: DimensionText = new DimensionText(x, y, `${width}x${height}mm`, false, true);
			partDim.textAnchor = anchor as 'middle' | 'end' | 'start';

			dimensionsForGlassParts.push({ text: partDim, line: null });
		});
		return dimensionsForGlassParts;
	}

	getSpaceHeightDimensionsForGlassWall(glassWall: GlassWall | GlassWallDiamond, area: AreaType){
		var dims: IDimensionsGroup[] = [];
		const columnRect = this.profileService.getCornerColumn(area)[RectName.get(area)];
		const glassBotY = glassWall.points[SvgParams.LEFT_BOTTOM_POINT_NUM].y + GLASS_PART_PADDING;
		const glassTopY = glassWall.points[SvgParams.LEFT_TOP_POINT_NUM].y - GLASS_PART_PADDING;
		const colTopY = columnRect.y;
		const colBotY = columnRect.h + columnRect.y;

		var x1 = columnRect.x - 6;
		var x = x1 - 5;

		if (area == AreaType.Left) {
			x1 = columnRect.x + columnRect.w + 6;
			x = x1 + 15;
		}
		const x2 = x1;

		if (glassBotY != colBotY) {
			const newDimensionLine: DimensionLine = new DimensionLine(x1, glassBotY, x2, colBotY, DimensionLineParams.SECOND_LEVEL_DIMENSION, ElementSide.Left);
	
			const y = colBotY + (glassBotY - colBotY) / 2;
			const length = Math.abs(Common.round((glassBotY - colBotY) / SvgParams.SCALE - GLASS_PART_PADDING));

			if (length > 50) {
				const newDimensionText: DimensionText = new DimensionText(x, y, length, true, false);
				dims.push({ text: newDimensionText, line: newDimensionLine });
			}
		}

		if (glassTopY != colTopY && !(glassWall instanceof GlassWallDiamond) && glassWall.locationY != glassWall.minLocationY) {
			
			const newDimensionLine: DimensionLine = new DimensionLine(x1, glassTopY, x2, colTopY, DimensionLineParams.SECOND_LEVEL_DIMENSION, ElementSide.Left);

			const y = colTopY + (glassTopY - colTopY) / 2;
			const length = Math.abs(Common.round((glassTopY - colTopY) / SvgParams.SCALE - GLASS_PART_PADDING));

			if (length > 50) {
				const newDimensionText: DimensionText = new DimensionText(x, y, length, true, false);
				dims.push({ text: newDimensionText, line: newDimensionLine });
			}
		}

		return dims;
	}

	getSpaceWidthDimensionsForGlassWall(glassWall: GlassWall | GlassWallDiamond, area: AreaType) {
		var dims: IDimensionsGroup[] = [];
		
		const glassLeftX = glassWall.points[SvgParams.LEFT_BOTTOM_POINT_NUM].x - GLASS_PART_PADDING;
		const glassRightX = glassWall.points[SvgParams.RIGHT_BOTTOM_POINT_NUM].x + GLASS_PART_PADDING;
		var leftSpaceX = 0, rightSpaceX = 0;
		const column = this.profileService.getCornerColumn(area);
		const y = column[RectName.get(area)].y + column[RectName.get(area)].h + 6;
		const rear = this.profileService.roofElements[ElementType.WallProfile].find((w: WallProfile) => w[RectName.get(area)] != null);

		switch (area) {
			case AreaType.Left:
				leftSpaceX = rear.rectOnLeft.x;
				rightSpaceX = column.rectOnLeft.x;
				break;
			case AreaType.Right:
				rightSpaceX = rear.rectOnRight.x + rear.rectOnRight.w;
				leftSpaceX = column.rectOnRight.x + column.rectOnRight.w;
				break;
			case AreaType.Front:
				leftSpaceX = glassLeftX;
				rightSpaceX = glassRightX;
				break;
			case AreaType.Rear:
				leftSpaceX = glassLeftX;
				rightSpaceX = glassRightX;
				break;
				// front i rear nieobsłużone
		}

		if (glassLeftX != leftSpaceX) {
			const newDimensionLine: DimensionLine = new DimensionLine(glassLeftX, y, leftSpaceX, y, DimensionLineParams.SECOND_LEVEL_DIMENSION, ElementSide.Bottom);
	
			const x = leftSpaceX + (glassLeftX - leftSpaceX) / 2;
			const length = Math.abs(Common.round((glassLeftX - leftSpaceX) / SvgParams.SCALE - GLASS_PART_PADDING));
			const newDimensionText: DimensionText = new DimensionText(x, y + 15, length, false, false);

			dims.push({ text: newDimensionText, line: newDimensionLine });
		}
		if (glassRightX != rightSpaceX) {
			const newDimensionLine: DimensionLine = new DimensionLine(glassRightX, y, rightSpaceX, y, DimensionLineParams.SECOND_LEVEL_DIMENSION, ElementSide.Bottom);
	
			const x = rightSpaceX + (glassRightX - rightSpaceX) / 2;
			const length = Math.abs(Common.round((glassRightX - rightSpaceX) / SvgParams.SCALE - GLASS_PART_PADDING));
			const newDimensionText: DimensionText = new DimensionText(x, y + 15, length, false, false);

			dims.push({ text: newDimensionText, line: newDimensionLine });
		}

		return dims;
	}

	//#endregion

	//#region Marquise | Door


	public getDimensionsForWings(wings: IRect[]): IDimensionsGroup[] {
		var dims: IDimensionsGroup[] = [];
		wings.forEach(wing => {
			const w = Math.round(wing.w / SvgParams.SCALE);
			const h = Math.round(wing.h / SvgParams.SCALE);
			const x = wing.x + wing.w / 2;
			const y = wing.y + wing.h / 2 + 15;
			var wingDim: DimensionText = new DimensionText(x, y, `${w}x${h}mm`, false, true);

			dims.push({ text: wingDim, line: null });
		});

		return dims;
	}

	public getDimensionsForVertical(elements: MarquiseVertical[] | Door[]): IDimensionsGroup[] {
		var dims: IDimensionsGroup[] = [];
		elements.forEach(m => {
			dims.push(this.getWidthDimensionsForVertical(m));
			dims.push(this.getHeightDimensionsForVertical(m));
			if (m instanceof Door) {
				dims.push(...this.getDimensionsForWings(m.wings));
			}
		});

		return dims;
	}

	private getWidthDimensionsForVertical(e: MarquiseVertical | Door): IDimensionsGroup {
		const rect = e instanceof MarquiseVertical ? 'rectOn' + e.area : 'rect';
		const hs = e instanceof MarquiseVertical ? 10 : 60;
		const x1 = e[rect].x;
		const y1 = e[rect].y + e[rect].h + hs;
		const x2 = e[rect].x + e[rect].w;
		const y2 = y1;
		const newDimensionLine: DimensionLine = new DimensionLine(x1, y1, x2, y2, DimensionLineParams.FIFTH_LEVEL_DIMENSION, ElementSide.Bottom);

		const x = x1 + (x2 - x1) / 2;
		const y = y1 + 15;
		const length = Common.round((x2 - x1) / SvgParams.SCALE);

		const newDimensionText: DimensionText = new DimensionText(x, y, length, false);
		const dimgr: IDimensionsGroup = { line: newDimensionLine, text: newDimensionText };

		return dimgr;
	}

	private getHeightDimensionsForVertical(e: MarquiseVertical | Door): IDimensionsGroup {
		const rect = e instanceof MarquiseVertical ? RectName.get(e.area) : 'rect';		
		const xs = e instanceof MarquiseVertical ? 10 : 6;
		const x1 = e.area === AreaType.Left ? e[rect].x - xs : e[rect].x + e[rect].w + xs;
		const y1 = e[rect].y;
		const x2 = x1;
		const y2 = e[rect].y + e[rect].h;
		const side = e.area === AreaType.Left ? ElementSide.Left : ElementSide.Right;
		const newDimensionLine: DimensionLine = new DimensionLine(x1, y1, x2, y2, DimensionLineParams.FIRST_LEVEL_DIMENSION, side);

		const x = e.area === AreaType.Left ? x1 - 5 : x1 + 12;
		const y = y1 + (y2 - y1) / 2;
		const length = Common.round(e[rect].h / SvgParams.SCALE);
		const newDimensionText: DimensionText = new DimensionText(x, y, length, true);

		const newDimensionGroup: IDimensionsGroup = {
			line: newDimensionLine,
			text: newDimensionText,
		};


		return newDimensionGroup;
	}

	public getDimensionsForMarquisesTop(marquises: MarquiseTop[]): IDimensionsGroup[] {
		var dims: IDimensionsGroup[] = [];
		marquises.forEach(m => {
			dims.push(this.getWidthDimensionsForMarquiseTop(m));
			const d = this.getHeightDimensionsForMarquise(m);
			if (d != null) {
				dims.push(d);
			}
		});

		return dims;
	}

	private getWidthDimensionsForMarquiseTop(e: MarquiseTop): IDimensionsGroup {
		const x1 = e.lineOnRoof.x1
		const y1 = e.lineOnRoof.y4 + 35;
		const x2 = e.lineOnRoof.x3;
		const y2 = y1;
		const newDimensionLine: DimensionLine = new DimensionLine(x1, y1, x2, y2, DimensionLineParams.FIRST_LEVEL_DIMENSION, ElementSide.Bottom);

		const x = x1 + (x2 - x1) / 2;
		const y = y1 + 15;
		const length = Common.round((x2 - x1) / SvgParams.SCALE);

		const newDimensionText: DimensionText = new DimensionText(x, y, length, false);
		const dimgr: IDimensionsGroup = { line: newDimensionLine, text: newDimensionText };

		return dimgr;
	}

	private getHeightDimensionsForMarquise(e: MarquiseTop): IDimensionsGroup {
		const x1 = e.lineOnRoof.x1 - 32;
		const y1 = e.lineOnRoof.y2;
		const x2 = x1;
		const y2 = e.lineOnRoof.y4;
		const newDimensionLine: DimensionLine = new DimensionLine(x1, y1, x2, y2, DimensionLineParams.SECOND_LEVEL_DIMENSION, ElementSide.Left);

		const x = x1 - 5;
		const y = y1 + (y2 - y1) / 2;
		const length = Common.round(e.getUIDepth());
		const newDimensionText: DimensionText = new DimensionText(x, y, length, true);

		const newDimensionGroup: IDimensionsGroup = {
			line: newDimensionLine,
			text: newDimensionText,
		};

		return newDimensionGroup;
	}

	//#endregion

	//#region Glass

	public getDimensionsForSpecificGlass(specificGlass: IGlassSpec): IDimensionsGroup[] {
		const dimensionsForSpecificGlass = [];
		dimensionsForSpecificGlass.push(this.getWidthDimensionsForSpecificGlass(specificGlass));
		dimensionsForSpecificGlass.push(this.getHeightDimensionsForSpecificGlass(specificGlass));

		return dimensionsForSpecificGlass;
	}

	private getWidthDimensionsForSpecificGlass(specificGlass: IGlassSpec): IDimensionsGroup {
		const x1 = SvgParams.START_X;
		const y1 = SvgParams.START_Y - 10;
		const x2 = SvgParams.START_X + specificGlass.width * SvgParams.SCALE;
		const y2 = SvgParams.START_Y - 10;
		const newDimensionLine: DimensionLine = new DimensionLine(x1, y1, x2, y2, DimensionLineParams.FIRST_LEVEL_DIMENSION, ElementSide.Top);

		const x = SvgParams.START_X + specificGlass.width * SvgParams.SCALE / 2;
		const y = SvgParams.START_Y - 20;
		const length = Common.round(specificGlass.width);
		const newDimensionText: DimensionText = new DimensionText(x, y, length, false);

		const newDimensionGroup: IDimensionsGroup = {
			line: newDimensionLine,
			text: newDimensionText,
		};

		return newDimensionGroup
	}

	private getHeightDimensionsForSpecificGlass(specificGlass: IGlassSpec): IDimensionsGroup {
		const x1 = SvgParams.START_X - 10;
		const y1 = SvgParams.START_Y + specificGlass.height * SvgParams.SCALE;
		const x2 = SvgParams.START_X - 10;
		const y2 = SvgParams.START_Y;
		const newDimensionLine: DimensionLine = new DimensionLine(x1, y1, x2, y2, DimensionLineParams.FIRST_LEVEL_DIMENSION, ElementSide.Left);

		const x = SvgParams.START_X - 20;
		const y = y2 + (y1 - y2) / 2;
		// const length = specificGlass.height;
		const length = Common.round(specificGlass.height / Math.cos(Common.toRadians(this.projectService.template.dropAngle)));
		const newDimensionText: DimensionText = new DimensionText(x, y, length, true);

		const newDimensionGroup: IDimensionsGroup = {
			line: newDimensionLine,
			text: newDimensionText,
		};

		return newDimensionGroup
	}

	//#endregion

	//#region Roof

	private displayDimensionsOnRoof() {
		if (this._showArea) {
			this.displayDimensionOfGlassOnRoof();
			this.displayDimensionOfRoofWindows();
			this.displayDimensionOfRoofVentilators();
			this.displayDimensionOfRoofGutters();
			this.displayDimensionOfWallGutters();
			this.displayDimensionOfConnectors();
			this.displayDimensionBetweenColumnsOnRoof();
			if (this.projectService.template.isStandalone) {
				this.displayDimensionBetweenRearColumns(AreaType.Roof);
			} else {
				this.displayDimensionOfRearLength();
			}
			this.displayDimensionOfRoofWidth();

			// https://trello.com/c/zCMLUAXC
			this.displayDimensionOfProjDepthTillColumnExt(); // Linia 1 (obowiazkowa) Do zew. krawedzi slupa
			// this.displayDimensionOfProjDepthTillColumnInt(); // Linia 2(obowiazkowa) Do wew.krawedzi slupa
			// usunite przez https://trello.com/c/CbmxBPv1
			this.displayDimensionOfProjDepthTillRinne(); // Linia 3 (opcjonalna, jezeli rynna jest cofnieta) Do zew. krawedzi rynny

			this.displayDimensionBetweenLedSpot();
		}

		if (this._showElements) {
			var topExists = false;
			this.getDimensionsForMarquisesTop(this.profileService.roofElements[ElementType.MarquiseTopUp]).forEach(d => {
				this.profileService.roofElements[ElementType.Dimension].push(d);
				topExists = true;
			});
			if (!topExists) {
				this.getDimensionsForMarquisesTop(this.profileService.roofElements[ElementType.MarquiseTopBottom]).forEach(d => {
					this.profileService.roofElements[ElementType.Dimension].push(d);
				});
			}
		}
	}

	private displayDimensionOfRoofWidth() {
		if (!(this.projectService.template.isStandalone || this.profileService.roofElements[ElementType.WallProfile].length > 1)) {
			return;
		}
		const rear = this.profileService.roofElements[ElementType.WallProfile][0];
		const distance = this.projectService.template.isStandalone ? DimensionLineParams.FIFTH_LEVEL_DIMENSION : DimensionLineParams.FOURTH_LEVEL_DIMENSION + 10;
		const x1 = SvgParams.START_X;
		const y1 = rear.rectOnRoof.y - distance;
		const x2 = SvgParams.START_X + this.projectService.template.width * SvgParams.SCALE;
		const y2 = y1;
		const newDimensionLine: DimensionLine = new DimensionLine(x1, y1, x2, y2, distance, ElementSide.Top);

		const x = SvgParams.START_X + (x2 - x1) / 2;
		const y = y1 - 5;
		const newDimensionText: DimensionText = new DimensionText(x, y, this.projectService.template.width, false);

		const newDimensionGroup = {
			line: newDimensionLine,
			text: newDimensionText,
		};

		this.profileService.roofElements[ElementType.Dimension].push(newDimensionGroup);
	}

	private displayDimensionOfRearLength() {
		this.profileService.roofElements[ElementType.WallProfile].forEach((rear: WallProfile) => {
			const x1 = rear.rectOnRoof.x;
			const y1 = rear.rectOnRoof.y - DimensionLineParams.SECOND_LEVEL_DIMENSION;
			const x2 = rear.rectOnRoof.x + rear.rectOnRoof.w;
			const y2 = rear.rectOnRoof.y - DimensionLineParams.SECOND_LEVEL_DIMENSION;
			const newDimensionLine: DimensionLine = new DimensionLine(x1, y1, x2, y2, DimensionLineParams.SECOND_LEVEL_DIMENSION, ElementSide.Top);

			const x = rear.rectOnRoof.x + rear.rectOnRoof.w / 2;
			const y = rear.rectOnRoof.y - 30;
			const length = Common.round(rear.rectOnRoof.w / SvgParams.SCALE);
			const newDimensionText: DimensionText = new DimensionText(x, y, length, false);

			const newDimensionGroup = {
				line: newDimensionLine,
				text: newDimensionText,
			};

			this.profileService.roofElements[ElementType.Dimension].push(newDimensionGroup);
		});
	}

	private displayDimensionOfGlassOnRoof() {
		this.profileService.roofElements[ElementType.Glass].forEach((glass: Glass) => {
			const x = glass.rect.x + (glass.rect.w - glass.stat.paddingRight * SvgParams.SCALE) / 2;
			const width = Math.round(glass.rect.w / SvgParams.SCALE - glass.stat.paddingLeft - glass.stat.paddingRight);
			const height = Math.round(glass.rect.h / SvgParams.SCALE - glass.stat.paddingTop - glass.stat.paddingBottom);

			if (height > 700) {
				const y3_4 = glass.marker.loc.y + 15;
				var glassDim: DimensionText = new DimensionText(x, y3_4, `${Math.round(glass.width)}x${Math.round(glass.length)}mm`, false, true);
				this.profileService.roofElements[ElementType.Dimension].push({ text: glassDim });
			}

			if (height > 600) {
				const yBottom = glass.rect.y + glass.rect.h - glass.stat.paddingBottom * SvgParams.SCALE - 20;
				var widthDim: DimensionText = new DimensionText(x, yBottom, `${width}mm`, false, true);
				this.profileService.roofElements[ElementType.Dimension].push({ text: widthDim });
				var lishmas: DimensionText = new DimensionText(x, yBottom + 12, Translations.Lichtmass, false, true);
				this.profileService.roofElements[ElementType.Dimension].push({ text: lishmas });
			}
		});
	}

	private displayDimensionOfRoofWindows() {
		if (!this.profileService.roofElements) {
			return;
		}

		const roofWindows = this.profileService.roofElements[ElementType.RoofWindow] as RoofWindow[];
		if (!roofWindows || roofWindows.length === 0) {
			return;
		}

		roofWindows.forEach((rw: RoofWindow) => {
			const x = rw.rect.x + rw.rect.w / 2;
			const y = rw.rect.y + rw.rect.h / 2;
			const width = Math.round(rw.glassWidth);
			const height = Math.round(rw.height);

			const labelDim: DimensionText = new DimensionText(x, y - 10, Translations.Dachfenster, false, true);
			labelDim.isLabel = true;
			if (this.profileService.roofElements) {
				this.profileService.roofElements[ElementType.Dimension].push({ text: labelDim });
			}

			const dimensionsDim: DimensionText = new DimensionText(x, y + 5, `${width}x${height}mm`, false, true);
			if (this.profileService.roofElements) {
				this.profileService.roofElements[ElementType.Dimension].push({ text: dimensionsDim });
			}
		});
	}

	private displayDimensionOfRoofVentilators() {
		if (!this.profileService.roofElements) {
			return;
		}

		const roofVentilators = this.profileService.roofElements[ElementType.RoofVentilator] as RoofVentilator[];
		if (!roofVentilators || roofVentilators.length === 0) {
			return;
		}

		roofVentilators.forEach((rv: RoofVentilator) => {
			const x = rv.rect.x + rv.rect.w / 2;
			const y = rv.rect.y + 10;
			const width = Math.round(rv.width);
			const height = Math.round(rv.height);

			const labelDim: DimensionText = new DimensionText(x, y - 2, Translations.Terrassenlufter, false, true);
			labelDim.isLabel = true;
			labelDim.fontSize = '6px';
			if (this.profileService.roofElements) {
				this.profileService.roofElements[ElementType.Dimension].push({ text: labelDim });
			}

			const dimensionsDim: DimensionText = new DimensionText(x, y + 5, `${width}x${height}mm`, false, true);
			dimensionsDim.fontSize = '8px';
			if (this.profileService.roofElements) {
				this.profileService.roofElements[ElementType.Dimension].push({ text: dimensionsDim });
			}
		});
	}

	private displayDimensionOfRoofGutters() {
		if (!this.profileService.roofElements) {
			return;
		}

		const roofGutters = this.profileService.roofElements[ElementType.RoofGutter] as RoofGutter[];
		if (!roofGutters || roofGutters.length === 0) {
			return;
		}

		roofGutters.forEach((rg: RoofGutter) => {
			const x = rg.rect.x + rg.rect.w / 2;
			const y = rg.rect.y + rg.rect.h / 2;
			const width = Math.round(rg.width);
			const height = Math.round(rg.height);

			const labelDim: DimensionText = new DimensionText(x, y - 10, Translations.Fallrohreinfassung, false, true);
			labelDim.isLabel = true;
			labelDim.fontSize = width > 700 ? '8px' : '6px';
			if (this.profileService.roofElements) {
				this.profileService.roofElements[ElementType.Dimension].push({ text: labelDim });
			}

			const dimensionsDim: DimensionText = new DimensionText(x, y + 5, `${width}x${height}mm`, false, true);
			if (this.profileService.roofElements) {
				this.profileService.roofElements[ElementType.Dimension].push({ text: dimensionsDim });
			}
		});
	}

	private displayDimensionBetweenRearColumns(side: AreaType) {
		var cols = this.profileService.getSortedColumns(CollectionName.get(AreaType.Roof), AreaType.Rear).filter(c => c.isRear).reverse();
		if (cols.length == 0) {
			return;
		}
		const lastC = cols[0];
		const firstC = cols[cols.length - 1];
		const by = this.projectService.CurrentAreaService.getShapeAreaPoints()[SvgParams.LEFT_TOP_POINT_NUM].y;

		this.displayDimensionBetweenExtremeColumns(firstC, lastC, by - 10, AreaType.Roof);
		if (cols.length == 2) {
			return;
		}

		const y1 = by - 25;
		const y2 = y1;
		const y = by - 30;

		let nc: ColumnProfile;
		if (lastC.rectOnRoof.x > SvgParams.START_X) {
			nc = new ColumnProfile(side, SvgParams.START_X - lastC.rectOnRoof.w / 2, lastC.rectOnRoof.y, lastC.length, side, lastC.configId, this.projectService.template);
			cols.splice(0, 0, nc);
		}

		const wi = this.projectService.template.width * SvgParams.SCALE;
		if (firstC.rectOnRoof.x + firstC.rectOnRoof.w < wi + SvgParams.START_X) {
			nc = new ColumnProfile(side, SvgParams.START_X + wi - firstC.rectOnRoof.w / 2, firstC.rectOnRoof.y, firstC.length, side, firstC.configId, this.projectService.template);
			cols.push(nc);
		}

		cols.forEach((c: ColumnProfile, index: number) => {
			if (index < cols.length - 1) {

				const nextC = cols[++index];

				const x1 = nextC.rectOnRoof.x + nextC.rectOnRoof.w / 2;;
				const x2 = c.rectOnRoof.x + c.rectOnRoof.w / 2;
				const newDimensionLine: DimensionLine = new DimensionLine(x1, y1, x2, y2, DimensionLineParams.SECOND_LEVEL_DIMENSION, ElementSide.Top, false);

				const x = x2 + (x1 - x2) / 2;
				const length = Math.abs(Common.round((x1 - x2) / SvgParams.SCALE));
				const newDimensionText: DimensionText = new DimensionText(x, y, length, false, true);

				const newDimensionGroup = {
					line: newDimensionLine,
					text: newDimensionText,
				};
				this.profileService.roofElements[ElementType.Dimension].push(newDimensionGroup);
			}
		});
	}

	private displayDimensionBetweenColumnsOnRoof() {
		const frontYPointOfFrontColumns = this.getYPointOfColumns(AreaType.Roof);
		var cols = this.profileService.getSortedColumns(CollectionName.get(AreaType.Roof), AreaType.Front);
		const firstC = cols[0];
		const lastC = cols[cols.length - 1];
		const by = 5;
		const rn = RectName.get(AreaType.Roof);

		if (cols.length > 2) {
			this.displayDimensionBetweenExtremeColumns(firstC, lastC, frontYPointOfFrontColumns, AreaType.Roof);
		}

		const y1 = frontYPointOfFrontColumns + by;
		const y2 = frontYPointOfFrontColumns + by;
		const y = frontYPointOfFrontColumns + by - 5;

		let nc: ColumnProfile;
		if (firstC.rectOnRoof.x > SvgParams.START_X) {
			nc = new ColumnProfile(AreaType.Roof, SvgParams.START_X - firstC.rectOnRoof.w / 2, firstC.rectOnRoof.y, firstC.length, AreaType.Roof, firstC.configId, this.projectService.template);
			cols.splice(0, 0, nc);
		}

		const wi = this.projectService.template.width * SvgParams.SCALE;
		if (lastC.rectOnRoof.x + lastC.rectOnRoof.w < wi + SvgParams.START_X) {
			nc = new ColumnProfile(AreaType.Roof, SvgParams.START_X + wi - lastC.rectOnRoof.w / 2, lastC.rectOnRoof.y, lastC.length, AreaType.Roof, lastC.configId, this.projectService.template);
			cols.push(nc);
		}

		cols.forEach((c: ColumnProfile, index: number) => {
			if (index < cols.length - 1) {
				const nextC = cols[++index];

				const x1 = nextC[rn].x + nextC[rn].w / 2;
				const x2 = c[rn].x + c[rn].w / 2;

				const x = x2 + (x1 - x2) / 2;
				const length = Common.round((x1 - x2) / SvgParams.SCALE);

				this.createAndStoreDimension(this.profileService[CollectionName.get(AreaType.Roof)], x1, x2, y1, y2, DimensionLineParams.SECOND_LEVEL_DIMENSION, x, y, length, "", ElementSide.Bottom, false);
			}
		});
	}

	private getYPointOfColumns(area: AreaType) {
		const ySetOfColumnsFronts = new Set<number>();
		const s = RectName.get(area);
		this.profileService.roofElements[ElementType.Column].forEach((c: ColumnProfile) => {
			if (area == AreaType.Roof) {
				ySetOfColumnsFronts.add(c.marker.loc.y);
			} else {
				if (c[s] != null) {
					ySetOfColumnsFronts.add(c[s].y + c[s].h);
				}
			}
		})

		return Array.from(ySetOfColumnsFronts).sort((y1: number, y2: number) => y1 < y2 ? 1 : -1)[0];
	}

	private getFilteredFrontColumnsByX(frontYPointOfFrontColumns: number, side: AreaType) {
		const s = 'rectOn' + side;
		const sideName = side == AreaType.Roof ? "roof" : "front";
		const allFrontColumns = this.profileService[sideName + 'Elements'][ElementType.Column].filter((c: ColumnProfile) => {
			if (side == AreaType.Roof) {
				return c.marker.loc.y === frontYPointOfFrontColumns;
			} else {
				return c[s].y + c[s].h === frontYPointOfFrontColumns
			}
		})

		return allFrontColumns.sort((c1: ColumnProfile, c2: ColumnProfile) => c1.rectOnRoof.x > c2.rectOnRoof.x ? 1 : -1);
	}

	private displayDimensionBetweenExtremeColumns(firstC: ColumnProfile, lastC: ColumnProfile, yPointOfColumns: number, area: AreaType) {
		const s = RectName.get(area);
		var by = area == AreaType.Roof ? (firstC.isFront ? 30 : -35) : 60 + this.foundHeight * SvgParams.SCALE;
		const x1 = lastC[s].x + lastC[s].w / 2;
		const y1 = yPointOfColumns + by;
		const x2 = firstC[s].x + firstC[s].w / 2;
		const y2 = y1;
		const side = firstC.isFront ? ElementSide.Bottom : ElementSide.Top;
		const newDimensionLine: DimensionLine = new DimensionLine(x1, y1, x2, y2, DimensionLineParams.THIRD_LEVEL_DIMENSION, side);

		const x = x2 + (x1 - x2) / 2;
		var y = yPointOfColumns + by - 5;
		const length = Math.abs(Common.round((x1 - x2) / SvgParams.SCALE));
		const newDimensionText: DimensionText = new DimensionText(x, y, length, false);
		newDimensionText.parathesis = true;

		const newDimensionGroup = {
			line: newDimensionLine,
			text: newDimensionText,
		};

		this.profileService[CollectionName.get(area)][ElementType.Dimension].push(newDimensionGroup);
	}

	private displayDimensionOfProjDepthTillColumnExt() {
		const front = this.profileService.getFrontOnSide(this.profileService.roofElements, Tags.LEFT_FRONT_TAG);
		const lefCol: ColumnProfile = this.profileService.roofElements[ElementType.Column].find((c: ColumnProfile) => c.tag === Tags.LEFT_COLUMN_TAG || c.tag == Tags.ANY_FRONT_TAG)

		const lvl = DimensionLineParams.THIRD_LEVEL_DIMENSION;
		const x1 = SvgParams.START_X - lvl;
		const y1 = lefCol.rectOnRoof.y + lefCol.rectOnRoof.h;
		const x2 = SvgParams.START_X - lvl;
		const y2 = SvgParams.START_Y;
		const newDimensionLine: DimensionLine = new DimensionLine(x1, y1, x2, y2, lvl, ElementSide.Left);

		const x = SvgParams.START_X - 50;
		const y = y2 + (y1 - y2) / 2;
		const length = Common.round((y1 - y2) / SvgParams.SCALE);
		const newDimensionText: DimensionText = new DimensionText(x, y, length, true, !!front.gutter);

		const newDimensionGroup = {
			line: newDimensionLine,
			text: newDimensionText,
		};

		this.profileService.roofElements[ElementType.Dimension].push(newDimensionGroup);
	}

	private displayDimensionOfProjDepthTillRinne() {
		const front = this.profileService.getFrontOnSide(this.profileService.roofElements, Tags.LEFT_FRONT_TAG);
		if (!front.gutter) {
			return;
		}

		const x1 = SvgParams.START_X - DimensionLineParams.FIFTH_LEVEL_DIMENSION;
		const y1 = front.gutter.rectOnRoof.y + front.gutter.rectOnRoof.h;
		const x2 = SvgParams.START_X - DimensionLineParams.FIFTH_LEVEL_DIMENSION;
		const y2 = SvgParams.START_Y;
		const newDimensionLine: DimensionLine = new DimensionLine(x1, y1, x2, y2, DimensionLineParams.FIFTH_LEVEL_DIMENSION, ElementSide.Left);

		const x = SvgParams.START_X - 80;
		const y = y2 + (y1 - y2) / 2;
		const length = Common.round((y1 - y2) / SvgParams.SCALE);
		const newDimensionText: DimensionText = new DimensionText(x, y, length, true);

		const newDimensionGroup = {
			line: newDimensionLine,
			text: newDimensionText,
		};

		this.profileService.roofElements[ElementType.Dimension].push(newDimensionGroup);
	}

	//#region LEDs

	private displayDimensionOfWallGutters() {
		if (!this.profileService.roofElements || !this.profileService.roofElements[ElementType.WallProfile]) {
			return;
		}

		const wallProfiles = this.profileService.roofElements[ElementType.WallProfile] as WallProfile[];
		if (!wallProfiles) {
			return;
		}

		wallProfiles.forEach((wp: WallProfile) => {
			if (!wp.wallGutters || wp.wallGutters.length === 0) {
				return;
			}

			wp.wallGutters.forEach((wg: WallGutterProfile) => {
				const x = wg.rectOnRoof.x + wg.rectOnRoof.w / 2;
				const y = wg.rectOnRoof.y - 10;
				const y1 = wg.rectOnRoof.y;
				const bendOffset = 25;

				const labelDim: DimensionText = new DimensionText(x + bendOffset, y - 5, Translations.Konstruktionsdurchfuhrung, false, true);
				labelDim.isLabel = true;
				labelDim.fontSize = '7px';

				const newDimensionLine: DimensionLine = new DimensionLine(x + bendOffset, y, x, y1, DimensionLineParams.FIRST_LEVEL_DIMENSION, ElementSide.Right);
				// newDimensionLine.bend = { x, y };
				newDimensionLine.noArrows = true;

				const newDimensionGroup = {
					line: newDimensionLine,
					text: labelDim,
				};

				if (this.profileService.roofElements && this.profileService.roofElements[ElementType.Dimension]) {
					this.profileService.roofElements[ElementType.Dimension].push(newDimensionGroup);
				}
			});
		});
	}

	private displayDimensionOfConnectors() {
		if (!this.profileService.roofElements) {
			return;
		}

		const columns = this.profileService.roofElements[ElementType.Column] as ColumnProfile[];

		columns.filter((c: ColumnProfile) => c.connector).forEach((col: ColumnProfile) => {
			let labelDim: DimensionText;
			let newDimensionLine: DimensionLine;
			const x = col.rectOnRoof.x + col.rectOnRoof.w / 2;

			if (col.isRear) {
				const y = col.rectOnRoof.y - 15;
				const y1 = col.rectOnRoof.y;

				labelDim = new DimensionText(x + 34, y - 2, Translations.Rinnenverbindung, false, true);
				newDimensionLine = new DimensionLine(x, y1, x + 32, y, DimensionLineParams.FIRST_LEVEL_DIMENSION, ElementSide.Right);
			} else {
				const y = col.rectOnRoof.y + col.rectOnRoof.h;	
				const y1 = y + 15;

				labelDim = new DimensionText(x + 34, y1 + 6, Translations.Rinnenverbindung, false, true);
				newDimensionLine = new DimensionLine(x, y, x + 32, y1, DimensionLineParams.FIRST_LEVEL_DIMENSION, ElementSide.Right);
			}


			labelDim.isLabel = true;
			labelDim.fontSize = '7px';
			newDimensionLine.noArrows = true;
			
			const newDimensionGroup = {
				line: newDimensionLine,
				text: labelDim,
			};

			if (this.profileService.roofElements) {
				this.profileService.roofElements[ElementType.Dimension].push(newDimensionGroup);
			}
		});
	}

	private displayDimensionBetweenLedSpot() {
		const allLeds = this.profileService.roofElements[ElementType.Led];

		if (!allLeds) {
			return
		}

		var barleds: Map<string, LED[]> = new Map<string, LED[]>();
		allLeds.forEach((led: LED) => {
			var key = led.circle.x.toString();
			if (barleds[key] == null) {
				barleds[key] = [];
			}
			barleds[key].push(led);
		});

		const rear = this.profileService.roofElements[ElementType.WallProfile][0];
		const front = this.profileService.roofElements[ElementType.Front][0];
		const rearLocation = rear.rectOnRoof.y + rear.rectOnRoof.h;
		const frontLocation = front.rectOnRoof.y;

		Object.keys(barleds).map(key => {
			const leds: LED[] = barleds[key];
			var first = leds[0];
			const last = leds[leds.length - 1];
			this.createDimensionForLedSpot(first.circle.x, first.circle.y, rearLocation);

			if (first != last) { // more than 1 led on a bar
				barleds[key].forEach((led: LED) => {
					this.createDimensionForLedSpot(led.circle.x, led.circle.y, first.circle.y);
					first = led;
				});
			}

			this.createDimensionForLedSpot(last.circle.x, frontLocation, last.circle.y);
		});

	}

	private createDimensionForLedSpot(xValue: number, y1Value: number, y2Value: number) {
		const y1 = y1Value;
		const y2 = y2Value;

		const x = xValue - 5;
		const y = y2 + (y1 - y2) / 2;
		const length = Common.round((y1 - y2) / SvgParams.SCALE);
		if (length == 0) {
			return;
		}
		const newDimensionText: DimensionText = new DimensionText(x, y, length, true, true);

		const newDimensionGroup = {
			line: null,
			text: newDimensionText,
		};

		this.profileService.roofElements[ElementType.Dimension].push(newDimensionGroup);
	}

	//#endregion

	//#endregion

	//#region Front

	private displayDimensionsOnStraight(area: AreaType, frontH: number, profile: Profile, lcTag: string) {


		this.foundHeight = 0;
		this.profileService.frontElements[ElementType.Column].forEach((c: ColumnProfile) => {
			if (c.foundation && c.foundation.size) {
				this.foundHeight = Math.max(this.foundHeight, c.foundation.size.height);
			}
		});

		if (this._showArea) {
			this.displayDimensionOfColumnLength(area, lcTag);
			this.displayDimensionOfColumnWidth(area);
			this.displayDimensionOfGlassWidth(area);
			this.displayDimensionBetweenColumns(area);
		}

		if (this._showElements) {
			this.displayVerticalsOnSide(area);
		}

	}

	private displayDimensionOfColumnLength(area: AreaType, tag: string) {
		var columns: ColumnProfile[] = [];
		this.profileService.roofElements[ElementType.Column].forEach(c => {
		 	if ((c.isFront && area == AreaType.Front) || (c.isRear && area == AreaType.Rear)) {
		 		columns.push(c);
		 	}
		})
		
		let diffColumns = [...new Set(columns.map(item => Math.round(item.length)))].length;
		let diffFoundations = [...new Set(columns.map(item => item.foundation?.size.height))].length;

		if (diffColumns == 1 && diffFoundations == 1) {
			if (area == AreaType.Rear) {
				this._displayColumnDimension(columns[columns.length - 1], area);
			} else {
				this._displayColumnDimension(columns[0], area);
			}
			
		} else {
			columns.forEach((c: ColumnProfile) => {
				this._displayColumnDimension(c, area);
			});
		}
	}

	private _displayColumnDimension(c: ColumnProfile, area: AreaType){
		const rn = RectName.get(area);
		const x1 = c[rn].x - DimensionLineParams.FIRST_LEVEL_DIMENSION;
		const y1 = c[rn].y + c[rn].h;
		const x2 = c[rn].x - DimensionLineParams.FIRST_LEVEL_DIMENSION;
		const y2 = c[rn].y;
		const newDimensionLine: DimensionLine = new DimensionLine(x1, y1, x2, y2, DimensionLineParams.THIRD_LEVEL_DIMENSION, ElementSide.Left);

		const x = c[rn].x - 20;
		const y = y2 + (y1 - y2) / 2;
		const length = Common.round(c.getLength());
		const newDimensionText: DimensionText = new DimensionText(x, y, length, true);

		const newDimensionGroup = {
			line: newDimensionLine,
			text: newDimensionText,
		};
		this.profileService[CollectionName.get(area)][ElementType.Dimension].push(newDimensionGroup);

		if (c.foundation) {
			this.displayDimensionOfFoundation(c, area);
		}
	}

	private displayDimensionOfFoundation(column: ColumnProfile, area: AreaType){
		const h = column.getHolder(area)[0];

		var x1 = h.rect.x - DimensionLineParams.FIRST_LEVEL_DIMENSION;
		if (area == AreaType.Left && column.tag == Tags.LEFT_COLUMN_TAG) {
			x1 = h.rect.x + h.rect.w + DimensionLineParams.FIRST_LEVEL_DIMENSION
		}
		else if (area == AreaType.Right && column.tag == Tags.RIGHT_REAR_COLUMN_TAG) {
			x1 = h.rect.x + h.rect.w + DimensionLineParams.FIRST_LEVEL_DIMENSION
		}
		const x2 = x1;

		const y1 = h.rect.y + h.rect.h;
		const y2 = h.rect.y;

		var side = ElementSide.Left;
		if ((area == AreaType.Right && column.isRear) || (area == AreaType.Left && column.isFront) ||
		(area == AreaType.Rear && column.tag == Tags.LEFT_REAR_COLUMN_TAG) || (area == AreaType.Front && column.tag == Tags.RIGHT_COLUMN_TAG)) {
			side = ElementSide.Right;
		}

		const dLine: DimensionLine = new DimensionLine(x1, y1, x2, y2, DimensionLineParams.FIRST_LEVEL_DIMENSION, side);
		const x = h.rect.x - 20;
		const y = y2 + (y1 - y2) / 2;
		const length = column.foundation.size.height;
		const dText: DimensionText = new DimensionText(x, y, length, true);

		const newDimensionGroup = {
			line: dLine,
			text: dText,
		};

		this.profileService[CollectionName.get(area)][ElementType.Dimension].push(newDimensionGroup);
	}

	private displayDimensionOfColumnWidth(area: AreaType) {
		const rn = RectName.get(area);
		this.profileService.roofElements[ElementType.Column].filter((c: ColumnProfile) => c[rn] != null).forEach(c => {
			const x1 = c[rn].x + c[rn].w;
			const x2 = c[rn].x;
			const y = c[rn].y + c[rn].h + 30;

			const x = x2 + (x1 - x2) / 2;
			const length = Common.round(c.width);
			const newDimensionText: DimensionText = new DimensionText(x, y, length, false, true);

			const newDimensionGroup = {
				line: null,
				text: newDimensionText,
			};

			this.profileService[CollectionName.get(area)][ElementType.Dimension].push(newDimensionGroup);
		});
	}

	private displayDimensionOfGlassWidth(area: AreaType) {

		var diff = 0;
		if (area == AreaType.Front) {
			diff = (this.projectService.template.backHeight - this.projectService.template.frontHeight) * SvgParams.SCALE;
		}		

		var elems = this.profileService[CollectionName.get(area)];

		const y1 = SvgParams.START_Y + diff  - DimensionLineParams.FIRST_LEVEL_DIMENSION;
		const y2 = y1;
		const y = SvgParams.START_Y + diff - 15;

		this.profileService.roofElements[ElementType.Glass].forEach((glass: Glass) => {
			const x1 = glass.rect.x + glass.stat.paddingLeft * SvgParams.SCALE;
			const x2 = glass.rect.x + glass.rect.w - glass.stat.paddingRight * SvgParams.SCALE;
			const newDimensionLine: DimensionLine = new DimensionLine(x1, y1, x2, y2, DimensionLineParams.FIRST_LEVEL_DIMENSION, ElementSide.Top);

			const x = glass.rect.x + (glass.rect.w - glass.stat.paddingRight * SvgParams.SCALE) / 2;
			const length = Common.round(glass.rect.w / SvgParams.SCALE - glass.stat.paddingLeft - glass.stat.paddingRight);
			const newDimensionText: DimensionText = new DimensionText(x, y, length, false);

			const newDimensionGroup = {
				line: newDimensionLine,
				text: newDimensionText,
			};

			elems[ElementType.Dimension].push(newDimensionGroup);
		});
	}

	private displayDimensionBetweenColumns(area: AreaType) {
		const yPointOfColumns = this.getYPointOfColumns(area);
		var cols = this.profileService.getSortedColumns(CollectionName.get(AreaType.Roof), area);
		const firstC = cols[0];
		const lastC = cols[cols.length - 1];
		const by = 25 + this.foundHeight * SvgParams.SCALE;
		const rn = RectName.get(area);

		if (cols.length > 2) {
			this.displayDimensionBetweenExtremeColumns(firstC, lastC, yPointOfColumns, area);
		}

		const y1 = yPointOfColumns + by;
		const y2 = yPointOfColumns + by;
		const y = yPointOfColumns + by + 15;

		let nc: ColumnProfile;
		if (firstC[rn].x > SvgParams.START_X) {
			nc = new ColumnProfile(area, SvgParams.START_X - firstC.rectOnRoof.w / 2, firstC.rectOnRoof.y, firstC.length, area, firstC.configId, this.projectService.template);
			cols.splice(0, 0, nc);
		}

		const wi = this.projectService.template.width * SvgParams.SCALE;
		if (lastC[rn].x + lastC[rn].w < wi + SvgParams.START_X) {
			nc = new ColumnProfile(area, SvgParams.START_X + wi - lastC.rectOnRoof.w / 2, lastC.rectOnRoof.y, lastC.length, area, lastC.configId, this.projectService.template);
			cols.push(nc);
		}

		cols.forEach((c: ColumnProfile, index: number) => {
			if (index < cols.length - 1) {
				const nextC = cols[++index];

				const x1 = nextC[rn].x + nextC[rn].w / 2;
				const x2 = c[rn].x + c[rn].w / 2;

				const x = x2 + (x1 - x2) / 2;
				const length = Math.abs(Common.round((x1 - x2) / SvgParams.SCALE));

				this.createAndStoreDimension(this.profileService[CollectionName.get(area)], x1, x2, y1, y2, DimensionLineParams.SECOND_LEVEL_DIMENSION, x, y, length, "", ElementSide.Bottom, false);
			}
		});
	}


	//#endregion

	private displayVerticalsOnSide(area: AreaType) {
		const collection = this.profileService[CollectionName.get(area)][ElementType.Dimension];
		var elements: VerticalElement[] = [];
		elements.push(...this.profileService.getVerticals(area, ElementType.GlassWallDiamond));
		elements.push(...this.profileService.getVerticals(area, ElementType.GlassWall));
		elements.push(...this.profileService.getVerticals(area, ElementType.MarquiseVertical));
		elements.push(...this.profileService.getVerticals(area, ElementType.Door));

		this.getOuterElements(elements, area).forEach(element => {
			if (element instanceof GlassWallDiamond || element instanceof GlassWall) {
				collection.push(...this.getWidthDimensionsForGlassWall(element, area));
				collection.push(...this.getHeightDimensionsForGlassWall(element, area));
				collection.push(...this.getDimentionsForGlassParts(element.parts, area));
			} else if (element instanceof MarquiseVertical) {
				collection.push(...this.getDimensionsForVertical([element]));
			} else if (element instanceof Door) {
				collection.push(...this.getDimensionsForVertical([element]));
			}
		});
	}

	private getOuterElements(elements: VerticalElement[], area: AreaType): VerticalElement[] {
		return elements.filter((element, index) => {
			const isConflict = elements.some((otherElement, otherIndex) => {
				if (index == otherIndex) {
					return false;
				}
	
				const rect1 = element.rect;
				const lx1 = rect1.x;
				const rx1 = rect1.x + rect1.w;
				const ty1 = rect1.y;
				const by1 = rect1.y + rect1.h;
				const rect2 = otherElement.rect;
				const lx2 = rect2.x;
				const rx2 = rect2.x + rect2.w;
				const ty2 = rect2.y;
				const by2 = rect2.y + rect2.h;
	
				const xOverlap = ((lx1 <= lx2 && lx2 <= rx1) || (lx1 <= rx2 && rx2 <= rx1) || (lx2 <= lx1 && lx1 <= rx2) || (lx2 <= rx1 && rx1 <= rx2));
				const yOverlap = ((ty1 <= ty2 && ty2 <= by1) || (ty1 <= by2 && by2 <= by1) || (ty2 <= ty1 && ty1 <= by2) || (ty2 <= by1 && by1 <= by2));
	
				if (xOverlap && yOverlap) {
					return otherElement.layer > element.layer;
				}
	
				return false;
			});
	
			return !isConflict;
		});
	}

	//#region Left

	private displayDimensionsOnLeftSide() {
		if (this._showArea) {
			this.displayDimensionOfDepthOnLeft();
			this.displayDimensionOfBackHeightOnLeft();
			this.displayDimensionOfFrontHeightOnLeft();
			this.displayDimensionOfFoundationOnLeftSide();
		}
		if (this._showElements) {
			this.displayVerticalsOnSide(AreaType.Left);
		}
		this.removeDuplicate(this.profileService.leftSideElements, this.leftColHeightIx, this.leftFrontHeightIx);
	};

	private displayDimensionOfDepthOnLeft() {
		const rear = this.profileService.leftSideElements[ElementType.WallProfile][0];
		const col: ColumnProfile = this.profileService.leftSideElements[ElementType.Column].find(c => c.tag == Tags.LEFT_COLUMN_TAG);

		const x1 = rear.rectOnLeft.x;
		const x2 = col.rectOnLeft.x + col.rectOnLeft.w;

		const level = DimensionLineParams.THIRD_LEVEL_DIMENSION;
		const level2 = level * this.getLevel2Factor();
		const y1 = SvgParams.START_Y - level;

		const x = SvgParams.START_X + (x2 - x1) / 2;
		const y = y1 - 12;
		const length = Common.round(x2 - x1) / SvgParams.SCALE;

		this.createAndStoreDimension(this.profileService.leftSideElements, x2, x1, y1, y1, level2, x, y, length, false, ElementSide.Top, false, level);
	}

	private displayDimensionOfBackHeightOnLeft() {
		const wallH = this.projectService.template.getRearSize().height;

		var x1 = this.getFoundationLineX(Tags.LEFT_REAR_COLUMN_TAG, AreaType.Left);
		var lvl = this.getFoundationLevel(Tags.LEFT_REAR_COLUMN_TAG, AreaType.Left);
		if (x1 == 0) {
			lvl = DimensionLineParams.SECOND_LEVEL_DIMENSION;
			x1 = SvgParams.START_X - lvl;
		}
		const y1 = SvgParams.START_Y + (wallH + this.projectService.template.backHeight) * SvgParams.SCALE;
		const y2 = SvgParams.START_Y + wallH * SvgParams.SCALE;

		const x = x1 - 12;
		const y = y1 + (y2 - y1) / 2;
		const length = Common.round(y1 - y2) / SvgParams.SCALE;
		this.createAndStoreDimension(this.profileService.leftSideElements, x1, x1, y1, y2, lvl, x, y, length, true, ElementSide.Left);

		const diff = this.projectService.template.isFlat ? 0 : this.projectService.template.backHeight - this.projectService.template.columnLength;
		var heightDiffText: DimensionText = new DimensionText(x, y2, diff, true, true);
		heightDiffText.textAnchor = "end";
		heightDiffText.parathesis = true;
		heightDiffText.drop = this.projectService.template.dropAngle;
		const heightDiff = {
			text: heightDiffText,
		};

		this.profileService.leftSideElements[ElementType.Dimension].push(heightDiff);

	}

	private getFoundation(colTag: string, area: AreaType) {
		const col: ColumnProfile = this.profileService.roofElements[ElementType.Column].find(c => c.tag == colTag);
		if (!col?.foundation?.size?.height || col.foundationOnConcrete()) {
			return null;
		}
		const holders = col.getHolder(area);
		if (!holders) {
			return null;
		}
		return holders[0];
	}

	private getFoundationLevel(colTag: string, area: AreaType) {
		const h = this.getFoundation(colTag, area);	
		if (h?.rect?.w > 40) {
			return DimensionLineParams.FOURTH_LEVEL_DIMENSION;
		}
		return DimensionLineParams.SECOND_LEVEL_DIMENSION;
	}
	private getFoundationLineX(colTag: string, area: AreaType) {
		const h = this.getFoundation(colTag, area);		
		if (!h) {
			return 0;
		}
		const col: ColumnProfile = this.profileService.roofElements[ElementType.Column].find(c => c.tag == colTag);
		if (area == AreaType.Left) {
			if (col.isFront) {
				return h.rect.x + h.rect.w + DimensionLineParams.FIRST_LEVEL_DIMENSION;
			} else {
				return h.rect.x - DimensionLineParams.FIRST_LEVEL_DIMENSION;
			}
		} else {
			if (col.isRear) {
				return h.rect.x + h.rect.w + DimensionLineParams.FIRST_LEVEL_DIMENSION;
			} else {
				return h.rect.x - DimensionLineParams.FIRST_LEVEL_DIMENSION;
			}
		}
	}

	private displayDimensionOfFrontHeightOnLeft() {
		const depth = this.projectService.template.depth;
		const wallH = this.projectService.template.getRearSize().height;
		const front = this.profileService.roofElements[ElementType.Front][0];
		const frontHeight = this.projectService.template.columnLength;

		var x1 = this.getFoundationLineX(Tags.LEFT_COLUMN_TAG, AreaType.Left);
		var lvl = this.getFoundationLevel(Tags.LEFT_COLUMN_TAG, AreaType.Left);
		if (x1 == 0) {
			x1 = SvgParams.START_X + depth * SvgParams.SCALE + DimensionLineParams.SECOND_LEVEL_DIMENSION;
		}

		const y2 = SvgParams.START_Y + (wallH + this.projectService.template.backHeight) * SvgParams.SCALE;
		const y1 = y2 - frontHeight * SvgParams.SCALE;

		const x = x1 + 15;
		const y = y1 + (y2 - y1) / 2;
		const length = Math.round(Common.round(y2 - y1) / SvgParams.SCALE);
		this.leftFrontHeightIx = this.createAndStoreDimension(this.profileService.leftSideElements, x1, x1, y1, y2, lvl, x, y, length, true, ElementSide.Right);
	}

	private displayDimensionOfFoundationOnLeftSide() {
		const columns: ColumnProfile[] = this.profileService.leftSideElements[ElementType.Column];
		columns.forEach((col: ColumnProfile) => {
			if (!col.foundation?.size?.height || col.foundationOnConcrete()) {
				return;
			}
			const holders = col.getHolder(AreaType.Left);
			if (!holders) {
				return;
			}
			const h = holders[0];
			const y1 = h.rect.y + h.rect.h;
			const y2 = h.rect.y;

			var x1: number;
			var side: ElementSide;
			var tx: number;
			if (col.isFront) {
				x1 = h.rect.x + h.rect.w + DimensionLineParams.FIRST_LEVEL_DIMENSION;
				side = ElementSide.Right;
				tx = x1 + 15;
			} else {
				x1 = h.rect.x - DimensionLineParams.FIRST_LEVEL_DIMENSION;
				side = ElementSide.Left;
				tx = x1 - 15;
			}

			const dLine = new DimensionLine(x1, y1, x1, y2, DimensionLineParams.FIRST_LEVEL_DIMENSION, side);
			const ty = y2 + (y1 - y2) / 2;
			const dText = new DimensionText(tx, ty, col.foundation.size.height, true);
			this.profileService.leftSideElements[ElementType.Dimension].push({ line: dLine, text: dText });
		});
	}

	//#endregion

	//#region Right

	private displayDimensionsOnRightSide() {
		if (this._showArea) {
			this.displayDimensionOfDepthOnRight();
			this.displayDimensionOfBackHeightOnRight();
			this.displayDimensionOfFrontHeightOnRight();
			this.displayDimensionOfFoundationOnRightSide();
		}
		if (this._showElements) {
			this.displayVerticalsOnSide(AreaType.Right);
		}
		this.removeDuplicate(this.profileService.rightSideElements, this.rightColHeightIx, this.rightFrontHeightIx);
	};

	private displayDimensionOfDepthOnRight() {
		const rear = this.profileService.rightSideElements[ElementType.WallProfile][0];
		const col: ColumnProfile = this.profileService.rightSideElements[ElementType.Column].find(c => c.tag == Tags.RIGHT_COLUMN_TAG);

		const x1 = col.rectOnRight.x;
		const x2 = rear.rectOnRight.x + rear.rectOnRight.w;

		const level = DimensionLineParams.THIRD_LEVEL_DIMENSION;
		const level2 = level * this.getLevel2Factor();
		const y1 = SvgParams.START_Y - level;

		const x = SvgParams.START_X + (x2 - x1) / 2;
		const y = y1 - 12;
		const length = Common.round(x2 - x1) / SvgParams.SCALE;
		this.createAndStoreDimension(this.profileService.rightSideElements, x2, x1, y1, y1, level, x, y, length, false, ElementSide.Top, false, level2);
	}

	private displayDimensionOfBackHeightOnRight() {
		const wallH = this.projectService.template.getRearSize().height;
		const depth = this.projectService.template.depth;
		const backHeight = this.projectService.template.backHeight;

		var x1 = this.getFoundationLineX(Tags.RIGHT_REAR_COLUMN_TAG, AreaType.Right);
		var lvl = this.getFoundationLevel(Tags.RIGHT_REAR_COLUMN_TAG, AreaType.Right);
		if (x1 == 0) {
			lvl = DimensionLineParams.SECOND_LEVEL_DIMENSION;
			x1 = SvgParams.START_X + depth * SvgParams.SCALE + lvl;
		}

		const y1 = SvgParams.START_Y + wallH * SvgParams.SCALE;
		const y2 = SvgParams.START_Y + (wallH + backHeight) * SvgParams.SCALE;

		const x = x1 + 20;
		const y = y1 + (y2 - y1) / 2;
		const length = Common.round(y2 - y1) / SvgParams.SCALE;

		this.createAndStoreDimension(this.profileService.rightSideElements, x1, x1, y1, y2, lvl, x, y, length, true, ElementSide.Right);

		const diff = this.projectService.template.isFlat ? 0 : this.projectService.template.backHeight - this.projectService.template.columnLength;
		var heightDiffText: DimensionText = new DimensionText(x, y1, diff, true, true);
		heightDiffText.textAnchor = "end";
		heightDiffText.parathesis = true;
		heightDiffText.drop = this.projectService.template.dropAngle;
		const heightDiff = {
			text: heightDiffText,
		};

		this.profileService.rightSideElements[ElementType.Dimension].push(heightDiff);

	}

	private displayDimensionOfFrontHeightOnRight() {
		const wallH = this.projectService.template.getRearSize().height;
		const front = this.profileService.roofElements[ElementType.Front][0];
		const frontHeight = this.projectService.template.columnLength;


		var x1 = this.getFoundationLineX(Tags.RIGHT_COLUMN_TAG, AreaType.Right);
		var lvl = this.getFoundationLevel(Tags.RIGHT_COLUMN_TAG, AreaType.Right);
		if (x1 == 0) {
			x1 = SvgParams.START_X - DimensionLineParams.SECOND_LEVEL_DIMENSION;
			lvl = DimensionLineParams.SECOND_LEVEL_DIMENSION;
		}
		const y1 = SvgParams.START_Y + (wallH + this.projectService.template.backHeight) * SvgParams.SCALE;
		const y2 = y1 - frontHeight * SvgParams.SCALE;

		const x = x1 - 15;
		const y = y2 + (y1 - y2) / 2;
		const length = Math.round(Common.round(y1 - y2) / SvgParams.SCALE);
		this.rightFrontHeightIx = this.createAndStoreDimension(this.profileService.rightSideElements, x1, x1, y1, y2, lvl, x, y, length, true, ElementSide.Left);
	}

	private displayDimensionOfFoundationOnRightSide() {
		const columns: ColumnProfile[] = this.profileService.rightSideElements[ElementType.Column];
		columns.forEach((col: ColumnProfile) => {
			if (!col.foundation?.size?.height || col.foundationOnConcrete()) {
				return;
			}
			const holders = col.getHolder(AreaType.Right);
			if (!holders) {
				return;
			}
			const h = holders[0];
			const y1 = h.rect.y + h.rect.h;
			const y2 = h.rect.y;

			var x1: number;
			var side: ElementSide;
			var tx: number;
			if (col.isRear) {
				x1 = h.rect.x + h.rect.w + DimensionLineParams.FIRST_LEVEL_DIMENSION;
				side = ElementSide.Right;
				tx = x1 + 15;
			} else {
				x1 = h.rect.x - DimensionLineParams.FIRST_LEVEL_DIMENSION;
				side = ElementSide.Left;
				tx = x1 - 15;
			}

			const dLine = new DimensionLine(x1, y1, x1, y2, DimensionLineParams.FIRST_LEVEL_DIMENSION, side);
			const ty = y2 + (y1 - y2) / 2;
			const dText = new DimensionText(tx, ty, col.foundation.size.height, true);
			this.profileService.rightSideElements[ElementType.Dimension].push({ line: dLine, text: dText });
		});
	}

	//#endregion

	private createAndStoreDimension(collection: any, x1, x2, y1, y2, level, tx, ty, length, transform, side: ElementSide, helper: boolean = false, levelEnd: number = level) {
		const newDimensionGroup = this.createDimension(x1, x2, y1, y2, level, tx, ty, length, transform, side, helper, levelEnd);
		collection[ElementType.Dimension].push(newDimensionGroup);
		return collection[ElementType.Dimension].length - 1; // Index of last element
	}

	private createDimension(x1, x2, y1, y2, level, tx, ty, length, transform, side: ElementSide, helper: boolean = false, levelEnd: number = level) {
		const newDimensionLine: DimensionLine = new DimensionLine(x1, y1, x2, y2, level, side, helper, levelEnd);
		const newDimensionText: DimensionText = new DimensionText(tx, ty, length, transform, helper);

		const newDimensionGroup = {
			line: newDimensionLine,
			text: newDimensionText,
		};

		return newDimensionGroup;
	}

	private removeDuplicate(collection: any, colIx: number, frontIx: number) {
		if (colIx >= 0 && frontIx >= 0) {
			const cDim: IDimensionsGroup = collection[ElementType.Dimension][colIx];
			const fDim: IDimensionsGroup = collection[ElementType.Dimension][frontIx];

			if (cDim && fDim && cDim.text.w == fDim.text.w) {
				collection[ElementType.Dimension].splice(colIx, 1);
			}
		}
	}

	private getLevel2Factor() {
		if (this.projectService.template.dropAngle > 7) {
			return 4
		}
		if (this.projectService.template.dropAngle > 4) {
			return 3
		}
		return 2;
	}
}
